import { useState, useEffect, useCallback } from "react";
import api from "../../services/api";

export default function InventoryTab({ currentCompanyId }) {
  const [products, setProducts] = useState([]);
  const [counts, setCounts]     = useState({});
  const [search, setSearch]     = useState("");
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [result, setResult]     = useState(null);
  const [error, setError]       = useState("");
  const [history, setHistory]   = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [catFilter, setCatFilter] = useState("");

  const loadProducts = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    api.stocks.products(currentCompanyId, { limit: 500 })
      .then(r => {
        const prods = r.products || [];
        setProducts(prods);
        const init = {};
        prods.forEach(p => { init[p.id] = String(p.stock_current); });
        setCounts(init);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const loadHistory = () => {
    api.stocks.inventoryHist(currentCompanyId)
      .then(setHistory)
      .catch(console.error);
    setShowHistory(true);
  };

  const categories = [...new Set(products.map(p => p.category))].sort();

  const filtered = products.filter(p =>
    (!search || p.name.toLowerCase().includes(search.toLowerCase()) || p.sku.toLowerCase().includes(search.toLowerCase())) &&
    (!catFilter || p.category === catFilter)
  );

  const changed = filtered.filter(p => parseFloat(counts[p.id]) !== p.stock_current);

  const handleSubmit = async () => {
    const changedItems = Object.entries(counts)
      .filter(([pid]) => {
        const p = products.find(pr => pr.id === parseInt(pid));
        return p && parseFloat(counts[pid]) !== p.stock_current;
      })
      .map(([pid, qty]) => ({ product_id: parseInt(pid), counted_quantity: parseFloat(qty) || 0 }));

    if (changedItems.length === 0) { setError("Aucun écart détecté."); return; }

    setSaving(true); setError(""); setResult(null);
    try {
      const res = await api.stocks.inventory(currentCompanyId, { counts: changedItems });
      setResult(res);
      loadProducts();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const diff = (pid) => {
    const counted = parseFloat(counts[pid]);
    const p = products.find(pr => pr.id === pid);
    if (!p || isNaN(counted)) return null;
    return counted - p.stock_current;
  };

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>Chargement…</div>;

  return (
    <div>
      {/* Résultat */}
      {result && (
        <div className="alert success" style={{ marginBottom: 16 }}>
          Inventaire enregistré — {result.count} ajustement(s) effectué(s).
          {result.adjustments?.map(a => (
            <div key={a.product_id} style={{ fontSize: 12, marginTop: 4 }}>
              {a.product_name} : {a.theoretical} → {a.counted} ({a.diff >= 0 ? "+" : ""}{a.diff.toFixed(2)})
            </div>
          ))}
        </div>
      )}
      {error && <div className="alert danger" style={{ marginBottom: 16 }}>{error}</div>}

      {/* Barre outils */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
        <input className="form-control" placeholder="🔍 Filtrer les produits…" style={{ flex: 1, minWidth: 200 }}
          value={search} onChange={e => setSearch(e.target.value)} />
        <select className="form-control" style={{ width: 160 }} value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="">Toutes catégories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button className="btn" onClick={loadHistory}>📋 Historique</button>
        <button className="btn" onClick={() => { const init = {}; products.forEach(p => { init[p.id] = String(p.stock_current); }); setCounts(init); setResult(null); }}>🔄 Réinitialiser</button>
        <button className="btn primary" onClick={handleSubmit} disabled={saving || changed.length === 0}>
          {saving ? "Enregistrement…" : `Valider ${changed.length} écart(s)`}
        </button>
      </div>

      <div className="card">
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
          Saisissez les quantités physiquement comptées. Les écarts sont calculés automatiquement.
        </div>
        <div className="tbl-overflow">
          <table className="tbl">
            <thead>
              <tr><th>SKU</th><th>Produit</th><th>Catégorie</th><th>Théorique</th><th>Compté</th><th>Écart</th></tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: "center", padding: 24, color: "var(--text-muted)" }}>Aucun produit</td></tr>
              ) : filtered.map(p => {
                const d = diff(p.id);
                const hasChange = d !== null && d !== 0;
                return (
                  <tr key={p.id} style={{ background: hasChange ? (d > 0 ? "#1a9e7208" : "#e24b4a08") : "transparent" }}>
                    <td style={{ fontFamily: "monospace", fontSize: 12, color: "var(--g)" }}>{p.sku}</td>
                    <td style={{ fontWeight: 600 }}>{p.name}{p.brand && <span style={{ fontWeight: 400, color: "var(--text-muted)", fontSize: 11 }}> · {p.brand}</span>}</td>
                    <td><span className="badge info">{p.category}</span></td>
                    <td style={{ fontWeight: 700 }}>{p.stock_current} {p.unit}</td>
                    <td>
                      <input
                        type="number"
                        step="0.001"
                        min="0"
                        className="form-control"
                        style={{ width: 100, textAlign: "right", fontWeight: hasChange ? 700 : 400, background: hasChange ? (d > 0 ? "#1a9e7215" : "#e24b4a15") : "#fff" }}
                        value={counts[p.id] ?? ""}
                        onChange={e => setCounts(c => ({ ...c, [p.id]: e.target.value }))}
                      />
                    </td>
                    <td style={{ fontWeight: 700, color: d === null ? "var(--text-muted)" : d === 0 ? "var(--text-muted)" : d > 0 ? "var(--g)" : "var(--red)" }}>
                      {d === null || d === 0 ? "—" : `${d > 0 ? "+" : ""}${d.toFixed(2)} ${p.unit}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Historique */}
      {showHistory && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowHistory(false)}>
          <div className="modal" style={{ maxWidth: 700 }}>
            <div className="modal-header">
              <h3 className="modal-title">📋 Historique des inventaires</h3>
              <button className="modal-close" onClick={() => setShowHistory(false)}>✕</button>
            </div>
            <div className="tbl-overflow" style={{ maxHeight: 400 }}>
              <table className="tbl">
                <thead><tr><th>Date</th><th>Produit</th><th>SKU</th><th>Écart</th></tr></thead>
                <tbody>
                  {history.length === 0 ? (
                    <tr><td colSpan={4} style={{ textAlign: "center", padding: 20, color: "var(--text-muted)" }}>Aucun inventaire enregistré</td></tr>
                  ) : history.map(m => (
                    <tr key={m.id}>
                      <td style={{ color: "var(--text-muted)" }}>{new Date(m.created_at).toLocaleDateString("fr-FR")}</td>
                      <td style={{ fontWeight: 600 }}>{m.product_name}</td>
                      <td style={{ fontFamily: "monospace", fontSize: 12 }}>{m.sku}</td>
                      <td style={{ fontWeight: 700, color: m.quantity >= 0 ? "var(--g)" : "var(--red)" }}>
                        {m.quantity >= 0 ? "+" : ""}{m.quantity}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ textAlign: "right", marginTop: 12 }}>
              <button className="btn" onClick={() => setShowHistory(false)}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
